import {useEffect, useState, useMemo} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import 'react-datepicker/dist/react-datepicker.css';
import { listReport } from '../../redux/actions/reportActions';
import { fetchSelectedBusiness } from '../../redux/actions/fetchSelectedBusinessActions';
import PdfReport from './PdfReport';
import Home from '../home/Home';
import SideBarSmallScreen from '../SideBarSmallScreen';
import '../../../src/styles/reports.css';

// import { AiOutlineBarChart } from 'react-icons/ai';
// import { MdBusinessCenter } from 'react-icons/md';
// import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { FaFileDownload } from 'react-icons/fa';
import { url } from '../../api/endpointURL';
import { webURL } from '../../config';

// Import generic components
import Select from '../../generic_components/Select/Select';
import DatePicker from '../../generic_components/DatePicker/DatePicker';
import Button from '../../generic_components/Button/Button';
import Card from '../../generic_components/Card/Card';
import Footer from '../../generic_components/Footer/Footer';

const Reports = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loadPDF, setLoadPDF] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(''); 
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('All');
  const [technicianSites, setTechnicianSites] = useState([]);
  const [userBusinessRole, setUserBusinessRole] = useState(null);
  const [exportFormat, setExportFormat] = useState('pdf'); // New state for export format
  const [isFullReport, setIsFullReport] = useState(false);

  const user = localStorage.getItem('email');

  // Total data
  const serversResponseData = useSelector(state => state.serversData);
  const servers = serversResponseData.servers;
  const isSmallScreen = window.innerWidth < 600;

  // Filter data based on business - total
  const handleBusinessChange = (event) => {
    setSelectedBusiness(event.target.value);
    setSelectedSite('All');
  };

  // Get the current user level for the business
  useEffect(() => {
    let userRole = localStorage.getItem('rbac_role')?.toLowerCase();
    setUserBusinessRole(userRole);
    if (userRole !== 'tla') {
      try {
        const businessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
        setUserBusinessRole(businessRoles.find(businessRole => businessRole.business === selectedBusiness)?.role?.toLowerCase());
      } catch (e) {
        console.error('Error parsing rbac_data:', e);
      }
    }
  }, [selectedBusiness]);

  // Function to fetch technician sites
  const fetchTechnicianSites = async (email) => {
    try {
      const response = await fetch(`${url}/technicianSites?email=${encodeURIComponent(email)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('No technician sites found for user');
          setTechnicianSites([]);
          return [];
        }
        throw new Error(`Failed to fetch technician sites: ${response.status}`);
      }
      
      const sites = await response.json();
      console.log('Fetched technician sites:', sites);
      setTechnicianSites(sites);
      return sites;
    } catch (error) {
      console.error("Error fetching technician sites:", error);
      setTechnicianSites([]);
      return [];
    }
  };

  // Fetch technician sites
  useEffect(() => {
    const email = localStorage.getItem('email');
    if (!email) return;
    if(userBusinessRole === 'technician'){
      fetchTechnicianSites(email);
    }
  }, [userBusinessRole]);

  // Filter technician sites
  const filteredTechnicianSites = useMemo(() => {
    if (!selectedBusiness) return [];
    return technicianSites?.filter(obj => obj.business === selectedBusiness) || [];
  }, [selectedBusiness, technicianSites]);

  // Extracting values from filtered sites
  const selectedTechnicianSites = useMemo(() => {
    if (!selectedBusiness) return [];
    return filteredTechnicianSites?.flatMap(obj => obj.site) || [];
  }, [selectedBusiness, filteredTechnicianSites]);
      
  const fetchUserResponse = useSelector(state => state.fetchUserResponse);
  const businesses = fetchUserResponse?.businesses;

  const filteredSites = businesses?.filter(obj => obj[selectedBusiness] !== undefined);
  
  const selectedSites = useMemo(() => {
    if (!selectedBusiness) return [];
    return filteredSites?.flatMap(obj => Object.values(obj))[0] || [];
  }, [selectedBusiness, filteredSites]);

  const sortedSites = useMemo(() => {
    return ['All', ...new Set(userBusinessRole === 'technician'
      ? selectedTechnicianSites.map((site) => site) 
      // : selectedSites.map((site) => site))].sort();
      : selectedSites.map((site) => site).sort())];
  }, [userBusinessRole, selectedTechnicianSites, selectedSites]);

  // let businessNames = businesses?.map(business => Object.keys(business)[0]);

  // Filter out businesses with no sites
  let businessesWithSites = businesses?.filter(business => Object.values(business)[0].length > 0);
  let businessNamesWithSites = businessesWithSites?.map(business => Object.keys(business)[0]);
  
  // businessNames = businessNames?.sort();
  businessNamesWithSites = businessNamesWithSites?.sort();
  
  // Filter data based on site
  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
  };

  // Handle export format change
  const handleExportFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  const dispatch = useDispatch();

    const handleDateChange = (e, field) => {
      if (field === 'fromDate') {
        setFromDate(e.target.value);
      } else {
        setToDate(e.target.value);
      }
    };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert('Please select dates.');
      return;
    }

    // Get user's local timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Create start of day and end of day in local time
    const localFrom = new Date(fromDate);
    localFrom.setHours(0, 0, 0, 0);

    const localTo = new Date(toDate);
    localTo.setHours(23, 59, 59, 999);

    // Convert to ISO string *without* shifting to UTC
    const fromLocalISOString = localFrom.toLocaleString('sv-SE', { timeZone }); // e.g., "2025-10-27 00:00:00"
    const toLocalISOString = localTo.toLocaleString('sv-SE', { timeZone });     // e.g., "2025-10-27 23:59:59"

    const reportData = {
      fromDate: fromLocalISOString,
      toDate: toLocalISOString,
      timeZone,                     // Include user’s timezone (optional but recommended)
      user,
      selectedBusiness,
      selectedSite
    };

    dispatch(listReport(reportData));
    setLoadPDF(true);
  };


  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setLoadPDF(false);
    setExportFormat('pdf');
    window.localStorage.setItem('fetch_report', false);
  };

  const handleToggleFullReport = () => {
    setIsFullReport(prev => !prev);
  };

    // console.log(`Line 193 the fromDate and toDate are ${fromDate} and ${toDate}`);

  useEffect(() => {
    localStorage.setItem('nav', 'report');
  }, []);

  useEffect(() => {
    if (servers.length) {
      setLoading(false);
    }
  }, [servers, loading]);    

  // Set first business as default selected
  useEffect(() => {
    if (businessNamesWithSites && !selectedBusiness) {
      setSelectedBusiness(businessNamesWithSites[0]);
      dispatch(fetchSelectedBusiness(businessNamesWithSites[0]));
    }      
  }, [selectedBusiness, businessNamesWithSites, dispatch]);
  
  // Fetch RBAC business-roles
  const fetchAndStoreRBAC = async (email) => {
    try {
      const url_path = url + '/fetch_rbac';
      const response = await fetch(url_path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user: email })
      });
      
      const rbacResponse = await response.json();
      localStorage.setItem('rbac_role', rbacResponse.global_role);
      localStorage.setItem('rbac_data', JSON.stringify(rbacResponse.business_roles));
      return rbacResponse;
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
    }
  };
  
  // SINGLE useEffect for initialization
  useEffect(() => {
    const email = localStorage.getItem('email');
    
    if (!email) {
      return;
    }
  
    const initializeData = async () => {
      try {
        await fetchAndStoreRBAC(email);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
  
    initializeData();
  }, []);

  const businessOptions = businessNamesWithSites?.map((business) => ({
      label: business,
      value: business
  }));

  const siteOptions = sortedSites.map((site) => ({
      label: site,
      value: site 
  }));


  return (
    <>
     {/* {!isSmallScreen &&  <div style={{ marginTop: '-3.5em' }}>
      <SideBar />
    </div>} */}
    { user ?
    <>
     {           
    <div className='report-main-container'>
      <div className='card-container-reports'>
        <Card className="form glass-card">

          <div className='report-container'>
              <div className='full-report-toggle'>
                <div className="full-report-title">
                  <h3>                            
                    Full report
                  </h3>
                </div>
                <div className="full-report-toggle-switch-container">
                  <label className="full-report-toggle-switch">
                      <input 
                        type="checkbox"
                        checked={isFullReport}
                        onChange={handleToggleFullReport}
                      />
                      <span className="full-report-slider"></span>
                  </label>
                </div>
              </div>

              <div className='report-header'>
                <div className='report-header-text'>
                  Health Alert Summary
                </div>
              </div>
            
              {/* select dropdown menu */}
              <Select
                label="Select a business: "
                className="report-info"
                value={selectedBusiness}
                onChange={handleBusinessChange}
                options={businessOptions}
              />

              {/* select site dropdown menu */}
              <br></br>
              <Select 
                label="Select a site: "
                className="report-info"
                value={selectedSite}
                onChange={handleSiteChange}
                options={siteOptions}
              />

              <br></br>
              <Form className='report-form-container'>
                <Form.Group controlId="fromDate">
                  <DatePicker
                    label="From date:"
                    value={fromDate}
                    onChange={(e) => handleDateChange(e, 'fromDate')}
                  />
                </Form.Group>
              
                <Form.Group controlId="toDate">
                  <br></br>
                  <DatePicker
                    label="To date:"
                    value={toDate}
                    onChange={(e) => handleDateChange(e, 'toDate')}
                  />
                </Form.Group>
                
                {/* Export Format Selection */}
                <Form.Group controlId="exportFormat" style={{ marginTop: '1rem' }}>
                  <Form.Label>
                    <FaFileDownload className='label-icons'/>
                    Export Format:
                  </Form.Label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    <Form.Check
                      type="radio"
                      label="PDF"
                      name="exportFormat"
                      value="pdf"
                      checked={exportFormat === 'pdf'}
                      onChange={handleExportFormatChange}
                      id="format-pdf"
                    />
                    <Form.Check
                      type="radio"
                      label="Excel"
                      name="exportFormat"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={handleExportFormatChange}
                      id="format-excel"
                    />
                    <Form.Check
                      type="radio"
                      label="CSV"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={handleExportFormatChange}
                      id="format-csv"
                    />
                  </div>
                </Form.Group>

                <br></br>
                <Button variant="primary" onClick={handleSubmit} className='form-btn-submit'>
                  Submit
                </Button>           
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <Button variant="blank" onClick={handleClear}>
                      Clear
                  </Button>
                </div>

                {loadPDF && (
                  <PdfReport 
                    fromDate={fromDate} 
                    toDate={toDate}
                    exportFormat={exportFormat}
                    selectedBusiness={selectedBusiness}
                    selectedSite={selectedSite}
                    siteOptions={siteOptions}
                    isFullReport={isFullReport}
                  />
                )}
              </Form>
          </div>
        </Card>
    </div>

    {isSmallScreen ? (
      <div className="reports-sidebar-small"><SideBarSmallScreen/></div>
          ) : null
    }     
    </div>    
    }
    <div className='footer-container-report'>
      <Footer url={webURL}/>
     </div>    
   </>
    :
    <Home />
    }

    </>
  );
};

export default Reports;